import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Button, Input, Skeleton, Space, Table, Typography, message } from "antd";
import { Link } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Chip from "@mui/material/Chip";
import { useSelector } from "react-redux";
import vehicleService from "../../service/vehicle.service";
import theme from "../../theme";
import QuickReboqueModal from "../../view/vehicle/components/QuickReboqueModal";
import {
  EMPTY_VEHICLE_FILTERS,
  VEHICLE_FILTER_FIELDS,
  VEHICLE_LIST_LIMIT,
  buildVehicleListParams,
  canDeleteVehicle,
  canEditVehicle,
  canViewVehicle,
  compareText,
  getVehicleClassification,
  normalizeVehicleSearchTerm,
  recordMatchesVehicleFilter,
  resolveFilterInputValue,
  resolveFilterSearchKeys,
  resolveVehicleListType,
  sortVehiclesByCreatedAtDesc,
  unwrapVehicleListItems,
  unwrapVehicleListTotal,
} from "../../util/vehicle/vehicle-list-utils";
import { GlobalSuccessModal, GlobalConfirmModal } from "../modalGlobal/modalGlobal";

const SEARCH_DEBOUNCE_MS = 400;
const SKELETON_MIN_VISIBLE_MS = 450;
const SKELETON_ROW_PREFIX = "__vehicle_skeleton__";
const SKELETON_ROW_COUNT = 10;

function renderClassificationChip(record) {
  const label = getVehicleClassification(record);
  const backgroundColor =
    label === "Reboque" ? theme.colors.highlight : theme.colors.primary;

  return (
    <Chip
      label={label}
      sx={{
        minWidth: "100px",
        maxWidth: "150px",
        backgroundColor,
        color: theme.colors.background,
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 600,
        height: 28,
        px: 0.5,
        boxShadow: theme.elevation.base,
        border: `1px solid ${theme.colors.primaryBorder}`,
        "& .MuiChip-label": {
          px: 1,
        },
      }}
    />
  );
}

function buildSkeletonRows(count = SKELETON_ROW_COUNT) {
  return Array.from({ length: count }, (_, index) => ({
    id: `${SKELETON_ROW_PREFIX}${index}`,
    __skeleton: true,
  }));
}

function skeletonCell(width = "72%") {
  return (
    <Skeleton
      active
      title={false}
      paragraph={{ rows: 1, width }}
      style={{ margin: 0 }}
    />
  );
}

function renderSkeletonForColumn(columnKey) {
  if (columnKey === "action") {
    return (
      <Space size={10}>
        <Skeleton.Avatar active size="small" shape="square" />
        <Skeleton.Avatar active size="small" shape="square" />
      </Space>
    );
  }
  if (columnKey === "classificacao") {
    return (
      <Skeleton.Button
        active
        size="small"
        style={{ width: 96, height: 28, borderRadius: 6 }}
      />
    );
  }

  const widths = {
    vehiclePlate: "58%",
    vehicleType: "70%",
    renavam: "75%",
    descricaoVeiculo: "85%",
    ufVeiculo: "40%",
    reboquesCount: "35%",
  };
  return skeletonCell(widths[columnKey] || "70%");
}

function hasActiveVehicleFilters(filters) {
  return VEHICLE_FILTER_FIELDS.some((field) =>
    Boolean(String(filters?.[field] ?? "").trim()),
  );
}

const DataVehicleTable = ({ refreshKey = 0, clearFiltersKey = 0 }) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [filters, setFilters] = useState(EMPTY_VEHICLE_FILTERS);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [tableResetKey, setTableResetKey] = useState(0);
  const [reboqueModalOpen, setReboqueModalOpen] = useState(false);
  const [editingReboqueId, setEditingReboqueId] = useState(null);
  const searchInput = useRef(null);
  const searchDebounceRef = useRef(null);
  const loadingStartedAtRef = useRef(Date.now());
  const fetchSeqRef = useRef(0);
  const user = useSelector((state) => state.user);

  const canView = canViewVehicle(user);
  const canEdit = canEditVehicle(user);
  const canDelete = canDeleteVehicle(user);
  const isFiltering = hasActiveVehicleFilters(filters);

  const beginLoading = useCallback(() => {
    loadingStartedAtRef.current = Date.now();
    setLoading(true);
  }, []);

  const endLoading = useCallback(async () => {
    const elapsed = Date.now() - loadingStartedAtRef.current;
    const remaining = SKELETON_MIN_VISIBLE_MS - elapsed;
    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining));
    }
    setLoading(false);
  }, []);

  const fetchData = useCallback(async () => {
    if (!user) return;

    const requestId = ++fetchSeqRef.current;
    beginLoading();
    try {
      const params = buildVehicleListParams(user, filters);
      if (!params) {
        if (requestId === fetchSeqRef.current) {
          setRows([]);
          setTotal(0);
        }
        return;
      }

      const response = await vehicleService.getAll({
        ...params,
        trailersFilter: "all",
      });
      if (requestId !== fetchSeqRef.current) return;

      const items = sortVehiclesByCreatedAtDesc(
        unwrapVehicleListItems(response),
      ).slice(0, VEHICLE_LIST_LIMIT);
      setRows(items);
      setTotal(unwrapVehicleListTotal(response, items));
    } catch (error) {
      if (requestId !== fetchSeqRef.current) return;
      console.error("Erro ao buscar veículos:", error);
      message.error(
        error?.response?.data?.message ||
          "Não foi possível carregar a lista de veículos.",
      );
      setRows([]);
      setTotal(0);
    } finally {
      if (requestId === fetchSeqRef.current) {
        await endLoading();
      }
    }
  }, [user, filters, beginLoading, endLoading]);

  useEffect(() => {
    fetchData();
  }, [user?.uid, user?.uidShipper, user?.viewALL, fetchData, refreshKey]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!clearFiltersKey) return;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    beginLoading();
    setFilters({ ...EMPTY_VEHICLE_FILTERS });
    setSearchText("");
    setSearchedColumn("");
    setTableResetKey((key) => key + 1);
  }, [clearFiltersKey, beginLoading]);

  const applyServerFilter = (dataIndex, rawValue) => {
    const value = normalizeVehicleSearchTerm(rawValue);
    // Abaixo do mínimo: não dispara busca no banco.
    if (value === null) return;

    beginLoading();
    setSearchText(value);
    setSearchedColumn(dataIndex);
    // Um campo por vez: evita AND acidental (ex.: renavam antigo + placa nova = vazio).
    setFilters({
      ...EMPTY_VEHICLE_FILTERS,
      [dataIndex]: value,
    });
  };

  const scheduleServerFilter = (dataIndex, rawValue) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const normalized = normalizeVehicleSearchTerm(rawValue);
    // Feedback imediato quando a busca vai disparar (3+ chars ou limpeza).
    if (normalized !== null) {
      beginLoading();
    }

    searchDebounceRef.current = setTimeout(() => {
      // Limpeza explícita (campo vazio) também recarrega a listagem.
      if (normalized === "") {
        beginLoading();
        setSearchText("");
        setSearchedColumn("");
        setFilters({ ...EMPTY_VEHICLE_FILTERS });
        return;
      }
      applyServerFilter(dataIndex, rawValue);
    }, SEARCH_DEBOUNCE_MS);
  };

  const applyColumnFilter = (selectedKeys, confirm, dataIndex) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    const rawValue = selectedKeys?.[0] != null ? String(selectedKeys[0]) : "";
    const value = normalizeVehicleSearchTerm(rawValue);
    if (value === null) {
      // Mantém o texto digitado no input, mas não busca abaixo do mínimo.
      return;
    }
    beginLoading();
    confirm();
    applyServerFilter(dataIndex, value);
  };

  const clearColumnFilter = (clearFilters) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    clearFilters?.();
    beginLoading();
    setSearchText("");
    setSearchedColumn("");
    setFilters({ ...EMPTY_VEHICLE_FILTERS });
  };

  const handleDeleteClick = (id) => {
    setVehicleToDelete(id);
    setOpenConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setOpenConfirmModal(false);
    if (!vehicleToDelete) return;

    try {
      await vehicleService.delete(vehicleToDelete);
      setOpenSuccessModal(true);
      fetchData();
    } catch (error) {
      console.error("Erro ao excluir veículo:", error);
    }
  };

  const openReboqueEditModal = (record) => {
    setEditingReboqueId(record?.id || null);
    setReboqueModalOpen(true);
  };

  const closeReboqueEditModal = () => {
    setReboqueModalOpen(false);
    setEditingReboqueId(null);
  };

  const handleReboqueSaved = () => {
    fetchData();
  };

  const wrapColumnRender = (columnKey, originalRender) => {
    return (value, record, index) => {
      if (record?.__skeleton) {
        return renderSkeletonForColumn(columnKey);
      }
      if (typeof originalRender === "function") {
        return originalRender(value, record, index);
      }
      return value;
    };
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => {
      const searchKeys = () =>
        resolveFilterSearchKeys(selectedKeys, filters[dataIndex]);

      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder="Buscar (mín. 3 caracteres)"
            value={resolveFilterInputValue(selectedKeys, filters[dataIndex])}
            onChange={(e) => {
              const nextValue = e.target.value;
              // Sempre [valor], inclusive "", para o input não voltar ao filtro antigo.
              setSelectedKeys([nextValue]);
              scheduleServerFilter(dataIndex, nextValue);
            }}
            onPressEnter={() =>
              applyColumnFilter(searchKeys(), confirm, dataIndex)
            }
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                applyColumnFilter(searchKeys(), confirm, dataIndex)
              }
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
              loading={loading}
            >
              Buscar
            </Button>
            <Button
              onClick={() => clearColumnFilter(clearFilters)}
              size="small"
              style={{ width: 90 }}
              disabled={loading}
            >
              Limpar
            </Button>
            <Button type="link" size="small" onClick={() => close()}>
              Fechar
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: () => (
      <SearchOutlined
        style={{ color: filters[dataIndex] ? "#1677ff" : undefined }}
      />
    ),
    // Busca remota + fallback local. Skeleton sempre passa no filtro da tabela.
    onFilter: (value, record) =>
      Boolean(record?.__skeleton) ||
      recordMatchesVehicleFilter(record, dataIndex, value),
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: wrapColumnRender(dataIndex, (text) =>
      searchedColumn === dataIndex && searchText ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
    ),
  });

  const columns = [
    {
      title: "Placa",
      dataIndex: "vehiclePlate",
      key: "vehiclePlate",
      width: 120,
      ...getColumnSearchProps("vehiclePlate"),
      sorter: (a, b) => compareText(a.vehiclePlate, b.vehiclePlate),
    },
    {
      title: "Tipo",
      dataIndex: "vehicleType",
      key: "vehicleType",
      width: 140,
      ...getColumnSearchProps("vehicleType"),
      sorter: (a, b) =>
        compareText(resolveVehicleListType(a), resolveVehicleListType(b)),
      render: wrapColumnRender("vehicleType", (_, record) => {
        const type = resolveVehicleListType(record);
        return searchedColumn === "vehicleType" && searchText ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={type}
          />
        ) : (
          type || null
        );
      }),
    },
    {
      title: "RENAVAM",
      dataIndex: "renavam",
      key: "renavam",
      width: 140,
      ...getColumnSearchProps("renavam"),
      sorter: (a, b) => compareText(a.renavam, b.renavam),
    },
    {
      title: "Descrição",
      dataIndex: "descricaoVeiculo",
      key: "descricaoVeiculo",
      width: 200,
      sorter: (a, b) => compareText(a.descricaoVeiculo, b.descricaoVeiculo),
      render: wrapColumnRender("descricaoVeiculo"),
    },
    {
      title: "UF",
      dataIndex: "ufVeiculo",
      key: "ufVeiculo",
      width: 72,
      render: wrapColumnRender("ufVeiculo"),
    },
    {
      title: "Reboques",
      dataIndex: "reboquesCount",
      key: "reboquesCount",
      width: 100,
      sorter: (a, b) => (a.reboquesCount || 0) - (b.reboquesCount || 0),
      render: wrapColumnRender("reboquesCount"),
    },
    {
      title: "Classificação",
      key: "classificacao",
      width: 130,
      render: wrapColumnRender("classificacao", (_, record) =>
        renderClassificationChip(record),
      ),
      sorter: (a, b) =>
        compareText(getVehicleClassification(a), getVehicleClassification(b)),
    },
    {
      title: "Ações",
      key: "action",
      width: 100,
      fixed: "right",
      render: wrapColumnRender("action", (_, record) => {
        const isReboque = getVehicleClassification(record) === "Reboque";

        return (
          <Space size="middle">
            {canEdit ? (
              isReboque ? (
                <EditOutlinedIcon
                  onClick={() => openReboqueEditModal(record)}
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <Link to={`/vehicle/${record.id}`}>
                  <EditOutlinedIcon />
                </Link>
              )
            ) : canView ? (
              <Link to={`/vehicle/${record.id}`} state={{ viewOnly: true }}>
                <VisibilityIcon />
              </Link>
            ) : null}
            {canDelete ? (
              <Link to="#">
                <DeleteOutlinedIcon
                  onClick={() => handleDeleteClick(record.id)}
                  color="error"
                  style={{ cursor: "pointer" }}
                />
              </Link>
            ) : null}
          </Space>
        );
      }),
    },
  ];

  const skeletonRows = useMemo(() => buildSkeletonRows(), []);
  const tableData = loading ? skeletonRows : rows;
  const statusLabel = isFiltering
    ? "Buscando veículos..."
    : "Carregando veículos...";

  return (
    <>
      {loading ? (
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginBottom: 10, fontSize: 13 }}
        >
          {statusLabel}
        </Typography.Text>
      ) : null}
      <Table
        key={tableResetKey}
        rowKey="id"
        columns={columns}
        dataSource={tableData}
        scroll={{ x: 1000 }}
        loading={{
          spinning: loading,
          tip: statusLabel,
        }}
        pagination={
          loading
            ? false
            : {
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: () => `${total} veículo(s)`,
              }
        }
      />
      <QuickReboqueModal
        open={reboqueModalOpen}
        reboqueId={editingReboqueId}
        onClose={closeReboqueEditModal}
        onSaved={handleReboqueSaved}
      />
      <GlobalConfirmModal
        open={openConfirmModal}
        handleClose={() => setOpenConfirmModal(false)}
        handleConfirm={handleConfirmDelete}
        title="Deseja mesmo excluir este veículo?"
      />
      <GlobalSuccessModal
        open={openSuccessModal}
        handleClose={() => setOpenSuccessModal(false)}
        title="Veículo excluído com sucesso!"
      />
    </>
  );
};

export default DataVehicleTable;
